import React from 'react';
import { useCurrencyOptional } from '../contexts/CurrencyContext';
import { formatUsdApproxRange, USD_APPROX_QUALIFIER } from '../lib/currency';

/**
 * CURR-01/CURR-03: the secondary "≈ $X" figure, and the only place a
 * buyer-facing USD number is produced.
 *
 * Deliberately append-only. It renders the USD figure and nothing else, so
 * every existing KES render site keeps its own markup — while KES is selected
 * this component returns null and the surface is byte-identical to before. That
 * is what keeps "KES is primary" true structurally rather than by convention:
 * the canonical figure is never routed through here, so it cannot be restyled,
 * reformatted, or replaced by accident.
 *
 * CURR-03 gives the figure a variant instead of one flat style, because the
 * right weight for it is not the same on a 200px card as beside a 30px price.
 * The variants only ever move USD between second and third place; none of them
 * can reach the KES figure above it.
 *
 * Returns null when there is no provider, no usable rate, KES is selected, the
 * listing is priced in another currency, or the conversion would round to $0.
 */

export type UsdApproxVariant = 'card' | 'detail' | 'sticky';

/**
 * One entry per surface that shows a USD figure. Everything about where USD
 * sits in the hierarchy is decided here and nowhere else — a call site chooses
 * a variant, not a font size, so no surface can quietly promote the
 * approximation past the KES price it belongs to.
 */
const VARIANT_CLASSES: Record<UsdApproxVariant, string> = {
  /**
   * Result cards. 14px medium, up from 12px normal after a PO review of the
   * live Home feed: at 12px grey-500 under a 20px bold price the figure was
   * legible in a screenshot and not on the page.
   *
   * 14px is the ceiling here, not a compromise. The three cards price at 20px
   * (list), 16px (grid) and 14px (spotlight), so anything larger would tie or
   * beat the KES figure on the smallest card. Weight and colour keep KES ahead
   * even where the sizes meet.
   */
  card: 'block text-sm font-medium text-gray-600 mt-0.5',
  /**
   * The detail page's third typographic step: below the 30px bold KES price,
   * clearly above the 14px muted fine print that follows it. Before this, the
   * USD figure rendered at the same 12–13px muted as the disclaimer, the
   * price/m², and the listed date — four things at one weight, so the only
   * number among them read as a footnote.
   *
   * Blue, from the same PO review. Size alone was not separating it from the
   * grey fine print underneath. `blue-600` is not a new colour — it is what
   * this screen already uses for Call Agent, the monthly estimate and the
   * mortgage figures, so the USD figure joins the page's existing accent
   * rather than introducing one.
   *
   * It does not threaten KES: 20px medium blue against 30px bold near-black
   * still reads second, and colour is the one axis that can raise attention
   * without raising rank.
   */
  detail: 'text-xl font-medium text-blue-600 leading-tight',
  /**
   * Mobile sticky bar — part of the detail screen, so it carries the same blue.
   *
   * Its own line under the KES figure rather than beside it: the bar's price
   * cell is roughly 110px wide between the CTAs at 375px, which a KES price
   * already fills, so an inline USD would push the canonical figure into the
   * ellipsis. Compact rounding keeps this line under nine characters, so it
   * never needs the width the KES figure is using.
   */
  sticky: 'block text-sm font-medium text-blue-600 truncate',
};

interface UsdApproxProps {
  /** Canonical price in KES. */
  amountKES: number;
  /** Upper bound, for range pricing. Ignored unless it exceeds `amountKES`. */
  maxAmountKES?: number | null;
  /** The listing's own currency code. Anything but KES suppresses the line. */
  currency?: string | null;
  /** Typographic step. See VARIANT_CLASSES. */
  variant?: UsdApproxVariant;
  /**
   * Show the inline "at today's rate" qualifier. Defaults on for `detail`,
   * which is the surface that used to carry the two-line disclaimer.
   */
  showQualifier?: boolean;
  className?: string;
}

const UsdApprox: React.FC<UsdApproxProps> = ({
  amountKES,
  maxAmountKES = null,
  currency = 'KES',
  variant = 'card',
  showQualifier,
  className = '',
}) => {
  const currencyCtx = useCurrencyOptional();
  if (!currencyCtx) return null;

  const low = currencyCtx.usdApproxFor(amountKES, currency);
  if (!low) return null;

  const hasRange = maxAmountKES != null && maxAmountKES > amountKES;
  const high = hasRange ? currencyCtx.usdApproxFor(maxAmountKES as number, currency) : null;

  // For a range, show USD only when BOTH ends convert. A half-converted range
  // ("≈ $8,000 – KES 2,500,000") is worse than no conversion at all.
  if (hasRange && !high) return null;

  // Reads "≈ $A – $B", and collapses to "≈ $A" when compact rounding lands both
  // ends on the same figure — a range that repeats itself denies its own spread.
  const text = hasRange ? formatUsdApproxRange(low, high as string) : low;

  const withQualifier = showQualifier ?? variant === 'detail';

  if (!withQualifier) {
    return <span className={`${VARIANT_CLASSES[variant]} ${className}`.trim()}>{text}</span>;
  }

  // The inline qualifier that replaced CURR-01's two-line disclaimer. The
  // toggle already says USD is selected and the `≈` already says the figure is
  // approximate, so the only job left is naming WHEN — which is the part a
  // buyer needs, because it is the part that changes tomorrow.
  //
  // `items-baseline` so it sits on the figure's baseline rather than floating
  // in the middle of it, and `flex-wrap` so a narrow phone drops it to its own
  // line instead of squeezing the number.
  return (
    <span className={`flex flex-wrap items-baseline gap-x-1.5 ${className}`}>
      <span className={VARIANT_CLASSES[variant]}>{text}</span>
      <span className="text-xs font-normal text-gray-500">{USD_APPROX_QUALIFIER}</span>
    </span>
  );
};

export default UsdApprox;
